"use client";

import { Toaster as Sonner, type ToastClassnames, type ToasterProps } from "sonner";
import { useTheme } from "@/lib/hooks/use-theme";

// Sonner styles toasts with unlayered rules that no utility can beat, so every
// override here is forced with `!`. `shadow-lg!` puts toasts on the elevation
// scale; that shadow also drew the focusable toast's focus ring, so the ring is
// re-added. Sonner draws the action button's focus as its own box-shadow, so
// that ring is forced too.
//
// Status toasts read the status tokens in place of sonner's rich colours:
// subtle fill, status border, on-subtle text. The icons draw in `currentColor`;
// the description and action button inherit it, so all follow the text. A
// default toast keeps a muted description under its title. `.dark` authors the
// subtle fills translucent, so each is layered as an image over the opaque
// popover background.
//
// Sonner declares its own `--success-bg`, `--info-bg`, `--warning-bg`,
// `--success-text`, `--info-text`, and `--warning-text` on the toaster, and
// `@theme inline` utilities read the variable where it is used, so inside a
// toast those names resolve to sonner's values. Reference the `--*-subtle` fills
// and the `*-on-subtle` text tokens (`--warning-foreground` for warning);
// `text-*-text` would draw sonner's colours.
export const toastClassNames = {
  toast:
    "shadow-lg! focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  description: "text-inherit!",
  actionButton:
    "border! border-current/30! bg-transparent! text-inherit! focus-visible:ring-2! focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  default: "[&_[data-description]]:text-muted-foreground!",
  success:
    "border-success/30! bg-[linear-gradient(var(--success-subtle),var(--success-subtle))]! text-success-on-subtle!",
  error:
    "border-destructive/30! bg-[linear-gradient(var(--destructive-subtle),var(--destructive-subtle))]! text-destructive-on-subtle!",
  warning:
    "border-warning/30! bg-[linear-gradient(var(--warning-subtle),var(--warning-subtle))]! text-warning-foreground!",
  info: "border-info/30! bg-[linear-gradient(var(--info-subtle),var(--info-subtle))]! text-info-on-subtle!",
} satisfies ToastClassnames;

const Toaster = ({ ...props }: Omit<ToasterProps, "toastOptions" | "richColors">) => {
  const { isDark } = useTheme();

  return (
    <Sonner
      className="toaster group font-sans!"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      toastOptions={{ classNames: toastClassNames }}
      theme={(isDark ? "dark" : "light") as ToasterProps["theme"]}
      {...props}
      richColors={false}
    />
  );
};

export { Toaster };
